import { memo, useMemo } from 'react';
import Images from '@assets/images';
import { IconNode, Typography } from '@components/base';
import {
  TRANSPORTATION_MODE,
  INCO_TERMS,
  DELIVERY_TERMS,
  TRANSPORTATION_MODE_ENUM
} from '@helpers/constants';
import { getFormattedDate, sanitizeValue, shouldShowPorts } from '@helpers/utils';
import css from '../index.module.scss';
import { PerformaInvoiceStates } from '@helpers/types/pi';

interface OrderDetailDynamicProps {
  previewData: PerformaInvoiceStates;
  setOrderState: (orderState: PerformaInvoiceStates) => void;
  invoiceType: string;
}

const helpers = {
  transportationMode: (p: OrderDetailDynamicProps) =>
    TRANSPORTATION_MODE.find((i) => i.value === p.previewData.orderDetails?.transportation_mode)
      ?.label ?? '-',
  incoTerm: (p: OrderDetailDynamicProps) =>
    INCO_TERMS.find((i) => i.value === p.previewData.orderDetails?.inco_terms)?.label ?? '-',
  qualityTesting: (p: OrderDetailDynamicProps) =>
    sanitizeValue(p.previewData.orderDetails?.place_of_quality_test),
  countryOfOrigin: (p: OrderDetailDynamicProps) =>
    p.previewData.orderDetails?.country_of_origin ?? '-',
  countryOfFinalDestination: (p: OrderDetailDynamicProps) =>
    p.previewData.orderDetails?.country_of_final_destination ?? '-',
  deliveryTerm: (p: OrderDetailDynamicProps) =>
    DELIVERY_TERMS.find((i) => i.value === p.previewData.orderDetails?.inco_terms)?.label ?? '-',
  portOfLoading: (p: OrderDetailDynamicProps) =>
    p.previewData.orderDetails?.port_of_loading_display_value &&
    p.previewData.orderDetails?.port_of_loading_country_display_value
      ? `${p.previewData.orderDetails.port_of_loading_display_value} - ${p.previewData.orderDetails.port_of_loading_country_display_value}`
      : '-',
  portOfDischarge: (p: OrderDetailDynamicProps) =>
    p.previewData.orderDetails?.port_of_discharge_display_value &&
    p.previewData.orderDetails?.port_of_discharge_country_display_value
      ? `${p.previewData.orderDetails.port_of_discharge_display_value} - ${p.previewData.orderDetails.port_of_discharge_country_display_value}`
      : '-',
  placeOfDelivery: (p: OrderDetailDynamicProps) =>
    p.previewData.orderDetails?.place_of_delivery ?? '-',
  paymentTerms: (p: OrderDetailDynamicProps) => p.previewData.orderDetails?.payment_terms ?? '-',
  showAdvance: (p: OrderDetailDynamicProps) =>
    p.previewData?.itemDetailsInfo?.include_advance_payment ? 'Yes' : 'No',
  blAwbNumber: (p: OrderDetailDynamicProps) =>
    p.previewData.orderDetails?.transportation_mode === TRANSPORTATION_MODE_ENUM.AIR
      ? p.previewData.orderDetails?.awb_number
      : p.previewData.orderDetails?.bl_number,
  blAwbDate: (p: OrderDetailDynamicProps) =>
    p.previewData.orderDetails?.transportation_mode === TRANSPORTATION_MODE_ENUM.AIR
      ? getFormattedDate(p.previewData.orderDetails?.awb_date)
      : getFormattedDate(p.previewData.orderDetails?.bl_date),
  awbServiceNumber: (p: OrderDetailDynamicProps) =>
    p.previewData.orderDetails?.awb_service_name ?? '-'
};

type HelperKey = keyof typeof helpers;

type FieldConfig = {
  key: HelperKey;
  label: string | ((p: OrderDetailDynamicProps) => string);
  visible?: (p: OrderDetailDynamicProps) => boolean;
};

const ORDER_FIELDS: FieldConfig[] = [
  { key: 'transportationMode', label: 'Mode of Transportation' },
  { key: 'incoTerm', label: 'Inco Terms' },
  { key: 'qualityTesting', label: 'Quality Testing' },
  { key: 'countryOfOrigin', label: 'Country of Origin' },
  { key: 'countryOfFinalDestination', label: 'Country of Final Destination' },
  { key: 'deliveryTerm', label: 'Delivery Terms' },
  { key: 'portOfLoading', label: 'Port of Loading' },
  { key: 'portOfDischarge', label: 'Port of Discharge' },
  { key: 'placeOfDelivery', label: 'Place of Delivery' },
  { key: 'paymentTerms', label: 'Payment Terms' },
  { key: 'showAdvance', label: 'Show advance amount in document' },
  {
    key: 'blAwbNumber',
    label: (p: OrderDetailDynamicProps) =>
      p.previewData.orderDetails?.transportation_mode === TRANSPORTATION_MODE_ENUM.AIR
        ? 'AWB Number'
        : 'BL Number',
    visible: (p: OrderDetailDynamicProps) => p.invoiceType === 'Commercial'
  },
  {
    key: 'blAwbDate',
    label: (p: OrderDetailDynamicProps) =>
      p.previewData.orderDetails?.transportation_mode === TRANSPORTATION_MODE_ENUM.AIR
        ? 'AWB Date'
        : 'BL Date',
    visible: (p: OrderDetailDynamicProps) => p.invoiceType === 'Commercial'
  },
  {
    key: 'awbServiceNumber',
    label: 'AWB Service Number',
    visible: (p: OrderDetailDynamicProps) =>
      p.invoiceType === 'Commercial' &&
      p.previewData.orderDetails?.transportation_mode === TRANSPORTATION_MODE_ENUM.AIR
  }
];

const OrderDetailDynamic = (props: OrderDetailDynamicProps) => {
  const { setOrderState, previewData } = props;

  const supplyType = previewData?.orderInfo?.order_type;

  const visibleFields = useMemo(() => {
    return ORDER_FIELDS.filter((f) => {
      if (f.key === 'portOfLoading' || f.key === 'portOfDischarge') {
        return shouldShowPorts(supplyType);
      }
      return f.visible ? f.visible(props) : true;
    });
  }, [props, supplyType]);

  return (
    <div className={css.outlineWrapper}>
      <div className={css.headerWrapper}>
        <Typography variant="pdoc">Order Details</Typography>

        <IconNode
          src={Images.editRed}
          alt="edit order details"
          className={css.editButton}
          component="button"
          onClick={() =>
            setOrderState({
              ...previewData,
              activeStep: 2
            })
          }
        />
      </div>
      <div className={css.gridContainer}>
        {visibleFields.map((field) => {
          const labelText = typeof field.label === 'function' ? field.label(props) : field.label;
          const rawValue = helpers[field.key](props);
          const values = Array.isArray(rawValue) ? rawValue : [rawValue];

          return (
            <div key={field.key} className={css.columnWrapper}>
              <Typography variant="ptext">{labelText}</Typography>
              {values.map((v, i) => (
                <Typography variant="pdoc" key={i}>
                  {sanitizeValue(v)}
                </Typography>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(OrderDetailDynamic);
